import { useEffect, useRef, useState } from 'react';
import { vehiclesService } from '../../lib/services';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 1;

function vehicleKey(v) {
  return `${v.make}|${v.model}|${v.trim || ''}`;
}

function vehicleLabel(v) {
  return v.trim ? `${v.make} ${v.model} ${v.trim}` : `${v.make} ${v.model}`;
}

/** Tolerant of the page-wrapped, bare-array, or bare-object response shapes. */
function unwrapList(resp) {
  const body = resp?.data ?? resp;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.content)) return body.content;
  return [];
}

export default function NewVehicleModelSelect({ onSelect, placeholder = 'Type a model…' }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (selected || query.trim().length < MIN_CHARS) {
      setResults([]);
      setSearched(false);
      setSearchError(false);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      setSearchError(false);
      try {
        const resp = await vehiclesService.list({ q: query.trim(), condition: 'NEW', limit: 20 });
        const rows = unwrapList(resp);
        const seen = new Map();
        rows.forEach((v) => { if (v.make && v.model) seen.set(vehicleKey(v), v); });
        setResults([...seen.values()]);
      } catch {
        setResults([]);
        setSearchError(true);
      } finally {
        setSearched(true);
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, selected]);

  const pick = (v) => {
    setSelected(v);
    setQuery(vehicleLabel(v));
    setOpen(false);
    onSelect({ make: v.make, model: v.model, variant: v.trim || '' });
  };

  const onChangeQuery = (e) => {
    const next = e.target.value;
    setQuery(next);
    setOpen(true);
    if (selected) {
      setSelected(null);
      onSelect({ make: '', model: '', variant: '' });
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        style={{
          width: '100%',
          padding: '7px 10px',
          fontSize: 12,
          color: 'var(--text-primary)',
          background: 'var(--bg-glass)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-sm)',
          outline: 'none',
        }}
        value={query}
        onChange={onChangeQuery}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && query.trim().length >= MIN_CHARS && !selected && (
        <div
          role="listbox"
          className="topbar-menu"
          style={{ left: 0, right: 'auto', width: '100%', minWidth: 240, maxHeight: 260, overflowY: 'auto' }}
        >
          {loading && (
            <div className="topbar-menu-item" style={{ cursor: 'default', opacity: 0.7 }}>Searching…</div>
          )}
          {!loading && searchError && (
            <div className="topbar-menu-item" style={{ cursor: 'default', opacity: 0.7, color: 'var(--danger)' }}>
              Search failed — try again
            </div>
          )}
          {!loading && !searchError && searched && results.length === 0 && (
            <div className="topbar-menu-item" style={{ cursor: 'default', opacity: 0.7 }}>
              No new models match &ldquo;{query.trim()}&rdquo;
            </div>
          )}
          {!loading && results.map((v) => (
            <button
              key={vehicleKey(v)}
              type="button"
              className="topbar-menu-item"
              onClick={() => pick(v)}
            >
              {vehicleLabel(v)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
